import React, { useCallback, useState } from "react";
import { Divider, Modal, Space } from "antd";
import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import { customDateFormatWithoutTime } from "components/Common/formatter";
import { USER_ROLES } from "utils/constants";
import classnames from "classnames";
import CustomLink from "components/Common/buttons/CustomLink";
import NGITooltip from "components/Common/utils/NGITooltip";

const useStyles = makeStyles({
  modal: {
    "& .ant-modal-content .ant-modal-body": {
      padding: "24px 50px !important",
    },
  },
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    borderBottom: "1px solid #0000000D",
    paddingBottom: 20,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  sectionTitle: {
    marginTop: 10,
    color: "#777777",
    fontWeight: 400,
    fontSize: 11,
  },
  infoSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    border: "1px solid #0000000F",
    borderRadius: 5,
    padding: 15,
    marginTop: 10,
  },
  infoTitle: {
    color: "#000000BF",
    fontWeight: 400,
  },
  infoData: {
    color: "#000000",
    fontWeight: 600,
  },
  primary: {
    color: "#0085FF",
  },
  noPrimary: {
    color: "#ff0000",
  },
});

const ReviewAssignmentModal = ({ item, onApproveDecline, onClose, hideActions = false }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [isApprove, setIsApprove] = useState(false);

  const openConfirm = useCallback(() => setIsOpen(true), []);
  const closeConfirm = useCallback(() => setIsOpen(false), []);

  const handleIsApprove = useCallback(state => {
    setIsApprove(state);
    openConfirm();
  }, []);

  const handleApproveDecline = useCallback(() => {
    closeConfirm();
    onApproveDecline(item, isApprove);
  }, [isApprove]);

  return (
    <Modal
      title={
        <div className={classes.titleWithDescription}>
          Review Pending Assignment
          <span>Review all data before approving or rejecting</span>
        </div>
      }
      open
      onCancel={onClose}
      closable={false}
      className={classes.modal}
      footer={[]}
    >
      <div>
        <div className={classes.sectionTitle}>Bundle Information</div>
        <div className={classes.infoSection}>
          <div className={classes.infoTitle}>Target Type</div>
          <div className={classes.infoData}>{item.type}</div>
        </div>

        {item.type === "Depot" && (
          <div className={classes.infoSection}>
            <div className={classes.infoTitle}>Selected Depots</div>
            <div className={classes.infoData}>{item?.depots?.join(", ")}</div>
          </div>
        )}
        {item.type === "CustomerGroup" && (
          <div className={classes.infoSection}>
            <div className={classes.infoTitle}>Customer Group</div>
            <div className={classes.infoData}>{item.customerGroup}</div>
          </div>
        )}
        {item.type === "Customer" && (
          <div className={classes.infoSection}>
            <div className={classes.infoTitle}>Customer ID</div>
            <div className={classes.infoData}>
              <NGITooltip title="Open Customer Details">
                <CustomLink color={"#0085FF"} underlined target="_blank" to={`/customers/customer/${item.customerId}`}>
                  {item.customerId}
                </CustomLink>
              </NGITooltip>
            </div>
          </div>
        )}

        <div className={classes.infoSection}>
          <div className={classes.infoTitle}>Bundle Assign Start Date</div>
          <div className={classes.infoData}>{item.activationDate}</div>
        </div>
        <div className={classes.infoSection}>
          <div className={classes.infoTitle}>Bundle Assign End Date</div>
          <div className={classes.infoData}>{item.deactivationDate || "-"}</div>
        </div>
        <div className={classes.infoSection}>
          <div className={classes.infoTitle}>Assigned as Primary</div>
          <div className={classnames(classes.infoData, item.isPrimary ? classes.primary : classes.noPrimary)}>
            {item.isPrimary ? "YES" : "NO"}
          </div>
        </div>
        <div className={classes.infoSection}>
          <div className={classes.infoTitle}>Bundle ID</div>
          <div className={classes.infoData}>
            <CustomLink color="#0085FF" underlined target="_blank" to={`/price-management/bundles/${item.bundleId}`}>
              {item.bundleId}
            </CustomLink>
          </div>
        </div>

        <div className={classes.sectionTitle}>Assigned By</div>
        <div className={classes.infoSection}>
          <div className={classes.infoTitle}>Assigned By</div>
          <div className={classes.infoData}>
            <NGITooltip title="Open User Details">
              <CustomLink color="#0085FF" underlined target="_blank" to={`/user/${item.updatedBy}`}>
                {item.updatedBy}
              </CustomLink>
            </NGITooltip>
          </div>
        </div>
        <div className={classes.infoSection}>
          <div className={classes.infoTitle}>Date of Assignment</div>
          <div className={classes.infoData}>{customDateFormatWithoutTime(item.updatedAt)}</div>
        </div>
      </div>

      <Divider />
      {!hideActions && (
        <Space>
          <CustomButton
            roles={[USER_ROLES.SYSTEM_FULL_ADMINS, USER_ROLES.FINANCE]}
            onClick={() => handleIsApprove(false)}
            type="primary"
            size="small"
            color="outlined-error"
            text="REJECT"
          />
          <CustomButton
            roles={[USER_ROLES.SYSTEM_FULL_ADMINS, USER_ROLES.FINANCE]}
            onClick={() => handleIsApprove(true)}
            type="primary"
            text="APPROVE"
            size="small"
          />
        </Space>
      )}

      {!!isOpen && (
        <ConfirmationModal
          onCancel={closeConfirm}
          onConfirm={handleApproveDecline}
          cancelBtnTitle={isApprove ? "Cancel" : "No"}
          confirmBtnTitle={isApprove ? "Confirm" : "Yes"}
          description={
            isApprove
              ? `Are you sure you want to do approve this bundle assignment? \n\nPlease confirm!`
              : "Are you sure you want to decline bundle assignment?"
          }
        />
      )}
    </Modal>
  );
};

export default ReviewAssignmentModal;
